/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, Modal } from "react-bootstrap";
import { useAuth } from "../../../../auth";
import { unsubscribePlan } from "../../../subscription/core/_requests";
import { ProfileDetails } from "../../core/_requests";
import { useFormattedUtils } from "../../../../component/dateUtils";

interface PlanUnsubscribeProps {
  showFinalModal?: boolean;
}

const FREE_PLAN_LIMIT = 10; // free plan allows only 10 dishes

const PlanUnsubscribe: React.FC<PlanUnsubscribeProps> = ({ showFinalModal = false }) => {
  const [showFinalConfirmationModal, setShowFinalConfirmationModal] = useState(showFinalModal);
  const { formatDateUS } = useFormattedUtils();
const [showActiveOrdersModal, setShowActiveOrdersModal] = useState(false);

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const chefId = currentUser?.uuid;
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showManageDishesModal, setShowManageDishesModal] = useState(false);
  const [listPlan, setListPlan] = useState<any>(null);
  const [listPlanDish, setListPlanDish] = useState<any>(null);

  // Fetch subscription details
  useEffect(() => {
    const fetchProfileDetails = async () => {
      try {
        const res = await ProfileDetails(chefId);
        setListPlan(res?.data?.chef_plan_subscriptions?.[0] || null);
        setListPlanDish(res?.data || null);
      } catch (error) {
        console.error("Error fetching profile details:", error);
      }
    };

    if (chefId) {
      fetchProfileDetails();
    }
  }, [chefId]);

  // ---------- Handlers ----------
  const handleShowUnsubscribeModal = () => setShowUnsubscribeModal(true);
  const handleCloseUnsubscribeModal = () => setShowUnsubscribeModal(false);
  const handleCloseFeaturesModal = () => setShowFeaturesModal(false);
  const handleCloseManageDishesModal = () => setShowManageDishesModal(false);
  const handleCloseFinalConfirmationModal = () => setShowFinalConfirmationModal(false);

  const handleConfirmUnsubscribe = () => {
    setShowUnsubscribeModal(false);
    setShowFeaturesModal(true);
  };

 const handleProceedWithFeaturesWarning = () => {
  setShowFeaturesModal(false);

  const totalDishCount = listPlanDish?.totalDishCount || 0;
  const activeOrdersCount = listPlanDish?.activeOrdersCount || 0;

  // 🔥 Priority Check 1: Active Orders Exist
  if (activeOrdersCount > 0) {
    setShowActiveOrdersModal(true);
    return;
  }

  // 🔥 Check 2: Too many dishes
  if (totalDishCount > FREE_PLAN_LIMIT) {
    setShowManageDishesModal(true);
    return;
  }

  // 🔥 All Good → Show Final Confirmation Modal
  setShowFinalConfirmationModal(true);
};


  const handleManageDishesOk = () => {
    setShowManageDishesModal(false);
    navigate("/settings/subscription-dish");
  };

  const handleProceedWithUnsubscribe = async () => {
    if (!listPlan) {
      toast.error("No plan selected for unsubscription.");
      return;
    }

    const { plan_id, amount, type, payment_id, validity } = listPlan;
    const data = { planId: plan_id, chefId, amount, type, validity, payment_id };

    try {
      setLoading(true);
      const res = await unsubscribePlan(data);
      const unsubscribeResponse = res?.data || res; // ensure axios/fetch compatibility

      if (unsubscribeResponse?.status) {
        toast.success(unsubscribeResponse?.message || "Unsubscribed successfully");
        setShowFinalConfirmationModal(false);
        navigate("/subscription");
        window.location.reload(); 
      } else {
        toast.error(unsubscribeResponse?.message || "Failed to unsubscribe");
      }
    } catch (error) {
      console.error("Error during unsubscribe process:", error);
      toast.error("Error during unsubscribe process. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => navigate("/subscription");

  // ---------- Render ----------
  return (
    <div className="col-12 mt-3">
      <div
        className="snippet-block h-100 d-flex align-items-center justify-content-between p-3"
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: "#f8f9fa",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div className="row w-100">
          <div className="col-8 d-flex align-items-center">
            <h6 className="fw-med mb-0 d-flex align-items-center">
              <img
                src="/assets/images/vector.png"
                alt="tick"
                className="me-2"
                style={{ width: "15px", height: "15px" }}
              />
              <span style={{ fontSize: "14px" }}>
                {listPlan?.plan?.name || "free plan "}
                {listPlan?.amount !== 0 && (
                  <i
                    className="material-symbols-outlined info-ico ms-2"
                    data-bs-toggle="tooltip"
                    title={`Name: ${listPlan?.plan?.name || ""}
Amount: $${listPlan?.amount?.toFixed(2) || "0.00"}
Start Date: ${formatDateUS(listPlan?.plan_start_date)}
End Date: ${formatDateUS(listPlan?.plan_end_date)}`}
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      verticalAlign: "middle",
                    }}
                  >
                    info
                  </i>
                )}
              </span>
            </h6>
          </div>

     <div className="col-4 d-flex justify-content-end align-items-center">
  {(listPlan?.amount > 0 && listPlan?.plan?.name !== "free plan" && listPlan?.type === 1 ) ? (
    <small
      className="text-danger"
      style={{ cursor: "pointer", textDecoration: "underline" }}
      onClick={handleShowUnsubscribeModal}
    >
      Unsubscribe
    </small>
  ) : (
    <small
      className="text-success"
      style={{ cursor: "pointer", textDecoration: "underline" }}
      onClick={handleUpgrade}
    >
      Upgrade
    </small>
  )}
</div>


        </div>
      </div>

      {/* Modals */}
      {/* Unsubscribe Confirmation Modal */}
      <Modal show={showUnsubscribeModal} onHide={handleCloseUnsubscribeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unsubscribe from {listPlan?.plan?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to unsubscribe from {listPlan?.plan?.name}?</p>
          {listPlan?.type === 1 && <p className="text-success">⚠️ Your subscription will end today.</p>}
          {listPlan?.type === 2 && (
            <p className="text-success">⚠️ Your plan will expire on {formatDateUS(listPlan?.plan_end_date)}.</p>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button className="btn btn-cancel rounded-5 px-5" onClick={handleCloseUnsubscribeModal}>
            No
          </Button>
          <Button className="btn btn-primary rounded-5 px-5" onClick={handleConfirmUnsubscribe}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
<Modal
  show={showActiveOrdersModal}
  onHide={() => setShowActiveOrdersModal(false)}
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>⚠️ Pending Orders</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <p className="text-danger">
      You have ongoing order(s). Please complete all pending orders before unsubscribing.
    </p>
  </Modal.Body>
  <Modal.Footer className="d-flex justify-content-center">
    <Button
      className="btn btn-primary rounded-5 px-5"
      onClick={() => {
        setShowActiveOrdersModal(false);
        navigate("/orders/view");
      }}
    >
      Go to Orders
    </Button>
  </Modal.Footer>
</Modal>
      {/* Features Modal */}
      <Modal show={showFeaturesModal} onHide={handleCloseFeaturesModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Unsubscribe Features</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Do you want to proceed with the unsubscription?</p>
          <Card>
            <Card.Body>
              <p className="text-danger">
                Unsubscribing will immediately end your access to all features, benefits, and privileges.
              </p>
              {listPlan?.type === 1 && <p className="text-success">Your subscription will end today.</p>}
              {listPlan?.type === 2 && (
                <p className="text-success">Your plan will expire on {formatDateUS(listPlan?.plan_end_date)}.</p>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button className="btn btn-cancel rounded-5 px-5" onClick={handleCloseFeaturesModal}>
            Cancel
          </Button>
          <Button className="btn btn-primary rounded-5 px-5" onClick={handleProceedWithFeaturesWarning}>
            Proceed
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Manage Dishes Modal */}
      <Modal show={showManageDishesModal} onHide={handleCloseManageDishesModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Manage Your Dishes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card>
            <Card.Body>
              <p className="text-danger">
                You currently have {listPlanDish?.totalDishCount || 0} dishes. The free plan allows only{" "}
                {FREE_PLAN_LIMIT}.
              </p>
              <p className="text-warning">
                Please delete {Math.max(0, (listPlanDish?.totalDishCount || 0) - FREE_PLAN_LIMIT)} extra dish
                {(listPlanDish?.totalDishCount || 0) - FREE_PLAN_LIMIT > 1 ? "es" : ""} before proceeding.
              </p>
              <p>Would you like to manage your dishes now?</p>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button className="btn btn-cancel rounded-5 px-5" onClick={handleCloseManageDishesModal}>
            No, Cancel
          </Button>
          <Button className="btn btn-primary rounded-5 px-5" onClick={handleManageDishesOk}>
            Yes, Manage Dishes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Final Confirmation Modal */}
      <Modal show={showFinalConfirmationModal} onHide={handleCloseFinalConfirmationModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>❗ Final Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to unsubscribe from the plan?</p>
          {listPlan?.type === 1 && <p className="text-success">Your subscription will end today.</p>}
          {listPlan?.type === 2 && (
            <p className="text-success">Your plan will expire on {formatDateUS(listPlan?.plan_end_date)}.</p>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button className="btn btn-cancel rounded-5 px-5" onClick={handleCloseFinalConfirmationModal}>
            No
          </Button>
          <Button
            className="btn btn-primary rounded-5 px-5"
            onClick={handleProceedWithUnsubscribe}
            disabled={loading}
          >
            {loading ? "Processing..." : "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Active Orders Block Modal */}


    </div>
  );
};

export default PlanUnsubscribe;